import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { companyAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { Loader2, ArrowLeft } from 'lucide-react';

export default function CompanyLogin() {
  const navigate = useNavigate();
  const { companyLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ phone: '', password: '' });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await companyAPI.login(form.phone, form.password);
      const data = response.data.data;
      if (!data || !data.token) { toast.error('Unexpected response'); return; }
      companyLogin(data);
      if (data.companyStatus === 'PENDING') {
        toast.info('Login successful. Account pending admin approval.');
      } else {
        toast.success('Welcome back, ' + data.contactPersonName + '!');
      }
      navigate('/company/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid phone or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">

      {/* ── LEFT: Branding ─────────────────────────────────────── */}
      <div className="hidden lg:flex w-[45%] flex-col justify-between px-16 py-12
        bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 25% 65%, rgba(37,99,235,0.2) 0%, transparent 60%),
                         radial-gradient(ellipse at 75% 25%, rgba(14,165,233,0.15) 0%, transparent 55%)`
          }} />

        {/* Logo */}
        <div className="relative">
          <img src="/logo-white.png" alt="ShramSena" className="h-11 w-auto" />
        </div>

        {/* Centre copy */}
        <div className="relative space-y-6">
          <div>
            <p className="text-blue-400 text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Organization Portal
            </p>
            <h2 className="text-4xl font-bold text-white leading-snug">
              Hire the right talent,<br />
              <span className="text-blue-400">every time.</span>
            </h2>
          </div>
          <p className="text-slate-400 text-base leading-relaxed max-w-sm">
            Access India's growing pool of admin-verified healthcare professionals.
            From ICU nurses to caregivers — we've done the vetting for you.
          </p>
          <div className="space-y-3 pt-2">
            {[
              'Document-verified professionals',
              'Admin screened & approved',
              'Shortlist and hire directly',
              'Trusted by hospitals & care homes',
            ].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/40
                  flex items-center justify-center shrink-0">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                </div>
                <span className="text-slate-300 text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-slate-600 text-xs">
          © {new Date().getFullYear()} ShramSena™ — Your Trusted Healthcare Staffing Partner
        </p>
      </div>

      {/* ── VERTICAL DIVIDER ───────────────────────────────────── */}
      <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent" />

      {/* ── RIGHT: Form ────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-12">
        <div className="w-full max-w-sm mx-auto">

          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-9 w-auto" />
          </div>

          <button onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600
              text-sm mb-10 transition group">
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Home
          </button>

          <div className="mb-8">
            <p className="text-blue-600 text-xs font-bold uppercase tracking-[0.15em] mb-2">
              Hospital &amp; Company
            </p>
            <h1 className="text-3xl font-bold text-slate-900 mb-1">Sign in</h1>
            <p className="text-gray-400 text-sm">
              Access your organization's hiring portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Registered Phone Number
              </label>
              <input
                name="phone" value={form.phone} onChange={handleChange}
                required placeholder="9876543210" maxLength={10}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  placeholder-gray-300 transition"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Password
              </label>
              <input
                name="password" type="password" value={form.password}
                onChange={handleChange} required placeholder="Enter your password"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  placeholder-gray-300 transition"
              />
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3.5 rounded-xl font-bold text-sm text-white
                bg-gradient-to-r from-blue-600 to-indigo-600
                hover:from-blue-500 hover:to-indigo-500
                shadow-lg shadow-blue-500/25
                flex items-center justify-center gap-2
                disabled:opacity-60 transition mt-2">
              {loading
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</>
                : 'Sign In to Organization Portal'}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100 space-y-3 text-sm text-gray-400">
            <p>
              Forgot your password?{' '}
              <Link to="/forgot-password?type=company" className="text-blue-600 font-semibold hover:underline">
                Reset via OTP
              </Link>
            </p>
            <p>
              New organization?{' '}
              <Link to="/company/register" className="text-blue-600 font-semibold hover:underline">
                Register here
              </Link>
            </p>
            <p>
              Healthcare professional?{' '}
              <Link to="/login" className="text-blue-600 font-semibold hover:underline">
                Staff login →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
